import {
  AssessmentGroupsQueryFilters,
  ExportMarkbookFilters,
  GetAssessmentsEntriesFilters,
  GetAssessmentsEntriesSort,
  GetMarkbookFilters,
} from '../../assessments';
import { Assessment as ApiV1Assessment } from '../../generated/schooly-api-v1/AssessmentRoute';
import { SelectList } from '../../generated/schooly-api-v1/SelectListRoute';
import { Api as ApiV2Assessment } from '../../generated/schooly-api-v2/ApiRoute';
import {
  AreaOfLearning,
  AssessmentAction,
  AssessmentMethod,
  ExportMarkbookInclude,
} from '../assessments';
import { Group } from '../groups';
import { SORT_DIRECTION, StringOrNull } from '../misc';
import { RecurringState } from '../recurring';
import { SchoolYear } from '../schools';
import { IColumnSort } from './people';

export interface AssessmentUpdate {
  name: string;
  assessment_date?: string | null;
  group_ids: string[];
  methods: AssessmentMethodUpdate[];
  display_name?: string;
  recurring_state: RecurringState | null;
  area_of_learning_name: AreaOfLearning['name'] | null;
}

export interface AssessmentMethodUpdate extends Omit<AssessmentMethod, 'id'> {
  method_id?: string;
}

export interface AssessmentUpdateResponse {
  success: string;
  assessment_id: string;
  entries_to_delete: number;
}

export interface AssessmentsGradeUpdateOption {
  id?: string;
  name: string;
  value: number;
  description?: StringOrNull;
}

export interface AssessmentsGradeUpdate {
  name: string;
  description: string;
  options: AssessmentsGradeUpdateOption[];
  active?: boolean;
}

export interface UpdateAssessmentGradeRequest {
  listId: string;
  grade: Partial<AssessmentsGradeUpdate>;
}

export interface CreateAssessmentGradeRequest {
  schoolId: string;
  grade: AssessmentsGradeUpdate;
}

export interface AssessmentCreateRequest {
  schoolId: string;
  assessment: AssessmentUpdate;
}

interface WithFollowing {
  withFollowing?: boolean;
}

export interface AssessmentUpdateRequest extends WithFollowing {
  assessmentId: string;
  assessment: AssessmentUpdate;
  confirmed?: boolean;
}
export interface AssessmentDeleteRequest extends WithFollowing {
  assessmentId: string;
  confirmed: boolean;
}

interface AssessmentCount {
  assessment_count: number;
}

export interface AssessmentDeleteResponse extends AssessmentCount {
  success: string;
  entries_to_delete?: number;
}

export interface AssessmentCreateResponse extends AssessmentCount {
  success: string;
  id: string;
  ids?: string[];
  recurrence_id?: string;
}

export interface AssessmentUpdateResponse extends AssessmentCount {
  success: string;
  entries_to_delete: number;
  will_overwrite: boolean;
}

export interface AssessmentForGroupExportRequest {
  groupId: string;
  date: string[];
  assessmentIds?: string[];
}

export interface AssessmentForRelationExportRequest {
  relationId: string;
  schoolYearIds: SchoolYear['id'][];
}

export interface AssessmentSingleExportRequest {
  assessmentId: string;
  groupIds: Group['id'][];
}
export interface GetAssessmentRequest {
  id: string;
}

export interface PerformAssessmentActionRequest {
  id: string;
  action: AssessmentAction;
}

export interface PerformAssessmentActionResponse {
  success: string;
}

export interface GetAssessmentGroupsRequest {
  assessment_id: string;
  filters: AssessmentGroupsQueryFilters;
  pageNumber?: number;
  pageSize?: number;
  sort?: IColumnSort<keyof Group>[];
  query?: string;
}

export interface GetAssessmentsEntriesParams {
  schoolId: string;
  pageSize?: number;
  pageNumber?: number;
  query?: string;
  sort?: GetAssessmentsEntriesSort[];
  filters?: GetAssessmentsEntriesFilters;
}

export interface GetMarkbookParams extends GetMarkbookRequestParams {
  pageSize?: number;
  pageNumber?: number;
  sort?: GetMarkbookSort[];
  filters?: GetMarkbookFilters;
  query?: GetMarkbookRequestQuery['search_query'];
  arrangeBy: GetMarkbookRequestQuery['arrange_by'];
}

export enum GetMarkbookSortBy {
  LastName = 'last_name',
  GivenName = 'given_name',
  Score = 'score',
  Grade = 'grade',
  Comment = 'comment',
}

export type GetMarkbookSortRegular = {
  sortBy: GetMarkbookSortBy.LastName | GetMarkbookSortBy.GivenName;
  direction: SORT_DIRECTION;
};

export type GetMarkbookSortWithParams = {
  sortBy: GetMarkbookSortBy.Comment | GetMarkbookSortBy.Grade | GetMarkbookSortBy.Score;
  direction: SORT_DIRECTION;
  assessmentId: string;
  subjectId: string;
};

export type GetMarkbookSort = GetMarkbookSortRegular | GetMarkbookSortWithParams;

export const isGetMarkbookSortWithParams = (
  sort: GetMarkbookSort,
): sort is GetMarkbookSortWithParams => {
  return (
    sort.sortBy === GetMarkbookSortBy.Comment ||
    sort.sortBy === GetMarkbookSortBy.Grade ||
    sort.sortBy === GetMarkbookSortBy.Score
  );
};

export interface ExportMarkbookParams extends ExportMarkbookRequestParams {
  filters: ExportMarkbookFilters;
  sort?: GetMarkbookSort[];
  query?: ExportMarkbookRequestQuery['search_query'];
  arrangeStudentsBy: ExportMarkbookRequestQuery['arrange_students_by'];
  arrangeAssessmentsBy: ExportMarkbookRequestQuery['arrange_assessments_by'];
  include: ExportMarkbookInclude[];
}

// Short aliases for autogenerated API types
export type CreateAssessmentResponseBody = ApiV1Assessment.ForSchoolCreate.ResponseBody;
export type UpdateAssessmentResponseBody = ApiV1Assessment.AssessmentPartialUpdate.ResponseBody;
export type DeleteAssessmentResponseBody = ApiV1Assessment.AssessmentDelete.ResponseBody;
export type AssessmentActionResponseBody = ApiV1Assessment.ActionCreate.ResponseBody;
export type EntriesForMethodRequestBody =
  ApiV1Assessment.EntriesForMethodForRelationCreate.RequestBody;
export type EntriesForMethodResponseBody =
  ApiV1Assessment.EntriesForMethodForRelationCreate.ResponseBody;

export type GetAssessmentsListRequestQuery =
  ApiV2Assessment.GetAssessmentsListApiV2AssessmentForSchoolSchoolIdGet.RequestQuery;
export type GetAssessmentsListResponseBody =
  ApiV2Assessment.GetAssessmentsListApiV2AssessmentForSchoolSchoolIdGet.ResponseBody;
export type GetSingleAssessmentRequestQuery =
  ApiV2Assessment.GetSingleAssessmentApiV2AssessmentAssessmentIdGet.RequestQuery;
export type GetAssessmentEntriesForSchoolResponseBody =
  ApiV2Assessment.GetAssessmentEntriesForSchoolApiV2AssessmentEntriesForSchoolSchoolIdGet.ResponseBody;
export type GetAssessmentsForProfileResponseBody =
  ApiV2Assessment.GetAssessmentsForProfileApiV2AssessmentForProfileProfileIdGet.ResponseBody;
export type GetAssessmentEntriesForProfileOverviewRequestQuery =
  ApiV2Assessment.GetAssessmentEntriesForProfileOverviewApiV2AssessmentForProfileProfileIdOverviewGet.RequestQuery;

export type SelectListCreateRequest = SelectList.ForSchoolCreate.RequestBody;
export type SelectListCreateResponse = SelectList.ForSchoolCreate.ResponseBody;
export type SelectListPartialUpdateRequest = SelectList.SelectListPartialUpdate.RequestBody &
  SelectList.SelectListPartialUpdate.RequestParams;
export type SelectListPartialUpdateResponse = SelectList.SelectListPartialUpdate.ResponseBody;
export type SelectListDeleteResponse = SelectList.SelectListDelete.ResponseBody;
export type SelectListListResponseBody = Array<SelectList.ForSchoolList.ResponseBody>;
export type SelectListDetailResponseBody = SelectList.SelectListDetail.ResponseBody;

export type GetMarkbookRequestQuery =
  ApiV2Assessment.GetMarkbookApiV2AssessmentMarkbookForSchoolSchoolIdGet.RequestQuery;
export type GetMarkbookRequestParams = Omit<
  ApiV2Assessment.GetMarkbookApiV2AssessmentMarkbookForSchoolSchoolIdGet.RequestParams,
  'schoolId'
> & {
  schoolId: string;
};
export type GetMarkbookResponseBody =
  ApiV2Assessment.GetMarkbookApiV2AssessmentMarkbookForSchoolSchoolIdGet.ResponseBody;
export type ExportMarkbookRequestQuery =
  ApiV2Assessment.ExportMarkbookApiV2ExportMarkbookForSchoolSchoolIdGet.RequestQuery;
export type ExportMarkbookRequestParams = Omit<
  ApiV2Assessment.ExportMarkbookApiV2ExportMarkbookForSchoolSchoolIdGet.RequestParams,
  'schoolId'
> & {
  schoolId: string;
};
export type ExportMarkbookResponseBody =
  ApiV2Assessment.ExportMarkbookApiV2ExportMarkbookForSchoolSchoolIdGet.ResponseBody;
export type GetAssessmentsAverageRequestQuery =
  ApiV2Assessment.GetAssessmentsAverageApiV2AssessmentForSchoolSchoolIdAverageGet.RequestQuery;
export type GetAssessmentsAverageRequestParams = Omit<
  ApiV2Assessment.GetAssessmentsAverageApiV2AssessmentForSchoolSchoolIdAverageGet.RequestParams,
  'schoolId'
> & {
  schoolId: string;
};
export type GetAssessmentsAverageResponse =
  ApiV2Assessment.GetAssessmentsAverageApiV2AssessmentForSchoolSchoolIdAverageGet.ResponseBody;
